import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { FiStar, FiLogOut, FiLock } from 'react-icons/fi';
import ReviewModerationPanel from '../components/ReviewModerationPanel';
import { StaffPhotoCapture, HostPhotosGallery } from '../components/VillaPhotos';
import TrialWipePanel from '../components/TrialWipePanel';

const API = process.env.REACT_APP_BACKEND_URL;

const VILLAS = [
  { id: '3bhk-garden-escape-lonavala', label: 'Artistic Villa (3BHK)' },
  { id: '4bhk-garden-retreat-lonavala', label: 'Reunion Villa (4BHK)' },
  { id: '5bhk-garden-delight-lonavala', label: 'Richie Villa (5BHK)' },
  { id: '12bhk-celebration-villa-lonavala', label: 'Celebration Villa (12BHK)' },
];

const CATEGORIES = [
  { key: 'cleaning', label: 'Cleaning', desc: 'Brooming, mopping, bathroom sanitation, spotlessness' },
  { key: 'communication', label: 'Communication', desc: 'Politeness, responsiveness, clarity during stay' },
  { key: 'checkin', label: 'Check-in Experience', desc: 'Timeliness, warm welcome, villa orientation' },
  { key: 'checkout', label: 'Checkout Experience', desc: 'Smooth handover, punctuality' },
  { key: 'upkeep', label: 'Housekeeping Upkeep', desc: 'Fresh linen, laundry quality, villa setup' },
];

const StarRow = ({ label, desc, value, onChange, disabled }) => (
  <div className="mb-4">
    <p className="text-[#1A1A2E] text-sm font-semibold">{label}</p>
    <p className="text-[#6B7280] text-xs mb-2">{desc}</p>
    <div className="flex gap-2">
      {[1, 2, 3, 4, 5].map(n => (
        <button key={n} disabled={disabled}
          onClick={() => onChange(n)}
          data-testid={`star-${label.toLowerCase().split(' ')[0]}-${n}`}
          className="p-1 disabled:opacity-40">
          <FiStar className={`w-8 h-8 ${value >= n ? 'text-[#F57C20] fill-[#F57C20]' : 'text-gray-300'}`} strokeWidth={2} />
        </button>
      ))}
    </div>
  </div>
);

function PublishFiveStar({ feedbackId, onDone, published }) {
  const [options, setOptions] = useState([]);
  const [chosen, setChosen] = useState('');
  const [guestName, setGuestName] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  useEffect(() => {
    fetch(`${API}/api/reviews/remark-options`)
      .then(r => r.json())
      .then(d => setOptions(d.options || []))
      .catch(() => {});
  }, []);

  const publish = async () => {
    setErr('');
    if (!chosen) return setErr('Please pick a remark to publish');
    setBusy(true);
    try {
      const res = await fetch(`${API}/api/reviews/publish`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ feedback_id: feedbackId, remark_id: chosen, guest_name: guestName })
      });
      if (!res.ok) { const d = await res.json().catch(() => ({})); throw new Error(d.detail || 'Publish failed'); }
      onDone && onDone();
    } catch (e) { setErr(e.message); }
    setBusy(false);
  };

  if (published) {
    return (
      <div className="mt-3 bg-[#0F1420] border border-green-500/40 rounded-lg p-3 text-left" data-testid="publish-success">
        <p className="text-green-400 text-sm font-semibold">Published to our Home page wall!</p>
        <p className="text-white/70 text-xs mt-1">Thank you for helping future guests decide.</p>
      </div>
    );
  }

  return (
    <div className="mt-3 bg-[#0F1420] border border-white/10 rounded-lg p-3 text-left" data-testid="publish-panel">
      <p className="text-[#F57C20] text-[10px] tracking-widest uppercase mb-2">Share your love publicly (optional)</p>
      <p className="text-white/70 text-xs mb-3">Pick one remark you agree with — we'll publish it on our Home page. Your name is optional.</p>
      <input
        type="text" value={guestName} onChange={e => setGuestName(e.target.value)}
        placeholder="Your first name (optional)" maxLength={40}
        data-testid="publish-guest-name"
        className="w-full bg-[#1B2432] text-white text-sm px-3 py-2 rounded border border-white/10 mb-3"
      />
      <div className="space-y-2 mb-3">
        {options.map(o => (
          <label key={o.id} className="flex items-start gap-2 text-white/80 text-xs cursor-pointer bg-[#1B2432] p-2 rounded border border-white/5 hover:border-[#F57C20]/40">
            <input type="radio" name="remark" value={o.id} checked={chosen === o.id}
              onChange={() => setChosen(o.id)}
              data-testid={`remark-option-${o.id}`}
              className="mt-0.5 accent-[#F57C20]" />
            <span>{o.text}</span>
          </label>
        ))}
        {!options.length && <p className="text-white/50 text-xs">Loading remarks…</p>}
      </div>
      {err && <p className="text-red-400 text-xs mb-2" data-testid="publish-error">{err}</p>}
      <button onClick={publish} disabled={busy || !chosen}
        data-testid="publish-btn"
        className="w-full bg-[#F57C20] hover:bg-[#e56d10] text-white text-sm font-bold py-2.5 rounded disabled:opacity-50">
        {busy ? 'Publishing…' : 'Publish to Home Page'}
      </button>
    </div>
  );
}

export default function FeedbackApp() {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [villaId, setVillaId] = useState('');
  const [guestPhone, setGuestPhone] = useState('');
  const [guestEmail, setGuestEmail] = useState('');
  const [ratings, setRatings] = useState({ cleaning: 0, communication: 0, checkin: 0, checkout: 0, upkeep: 0 });
  const [privateNote, setPrivateNote] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [justSubmitted, setJustSubmitted] = useState(null);
  const [stats, setStats] = useState(null);
  const [month, setMonth] = useState(() => new Date().toISOString().slice(0, 7));
  const [showStats, setShowStats] = useState(false);
  const [guestLoggedIn, setGuestLoggedIn] = useState(false);
  const [loginError, setLoginError] = useState('');

  // Phone-verify gate + PWA install
  const bindKey = `rgv_fb_bind_${token}`;
  const [verified, setVerified] = useState(() => !!localStorage.getItem(bindKey));
  const [verifyPhone, setVerifyPhone] = useState('');
  const [verifyErr, setVerifyErr] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [meta, setMeta] = useState(null);
  const [installPromptEvt, setInstallPromptEvt] = useState(null);
  const [installed, setInstalled] = useState(false);
  const [showIosHint, setShowIosHint] = useState(false);

  // Persist last-used token so the installed PWA's start_url can restore it
  useEffect(() => {
    if (token && verified) {
      try { localStorage.setItem('rgv_fb_last_token', token); } catch (e) { /* noop */ }
    }
  }, [token, verified]);

  // Inject PWA manifest + theme + iOS meta + register service worker
  useEffect(() => {
    let link = document.querySelector('link[rel="manifest"][data-rgv-fb="1"]');
    if (!link) {
      link = document.createElement('link');
      link.rel = 'manifest';
      link.setAttribute('data-rgv-fb', '1');
      document.head.appendChild(link);
    }
    link.href = '/rgv-feedback-manifest.json';

    let themeMeta = document.querySelector('meta[name="theme-color"][data-rgv-fb="1"]');
    if (!themeMeta) {
      themeMeta = document.createElement('meta');
      themeMeta.name = 'theme-color';
      themeMeta.content = '#F57C20';
      themeMeta.setAttribute('data-rgv-fb', '1');
      document.head.appendChild(themeMeta);
    }

    let apple = document.querySelector('link[rel="apple-touch-icon"][data-rgv-fb="1"]');
    if (!apple) {
      apple = document.createElement('link');
      apple.rel = 'apple-touch-icon';
      apple.href = '/admin-cal-icon-v2-180.png';
      apple.setAttribute('data-rgv-fb', '1');
      document.head.appendChild(apple);
    }

    const iosMetas = [
      { name: 'apple-mobile-web-app-capable', content: 'yes' },
      { name: 'apple-mobile-web-app-status-bar-style', content: 'black-translucent' },
      { name: 'apple-mobile-web-app-title', content: 'RGV Feedback' },
      { name: 'mobile-web-app-capable', content: 'yes' },
    ];
    const created = [];
    iosMetas.forEach(({ name, content }) => {
      if (!document.querySelector(`meta[name="${name}"][data-rgv-fb="1"]`)) {
        const m = document.createElement('meta');
        m.name = name;
        m.content = content;
        m.setAttribute('data-rgv-fb', '1');
        document.head.appendChild(m);
        created.push(m);
      }
    });

    if ('serviceWorker' in navigator) {
      navigator.serviceWorker
        .register('/rgv-feedback-sw.js', { scope: '/rgv-feedback/' })
        .catch((err) => console.warn('RGV Feedback SW registration failed:', err));
    }

    const prevTitle = document.title;
    document.title = 'RGV Feedback';

    return () => {
      try {
        if (link && link.parentNode) document.head.removeChild(link);
        if (themeMeta && themeMeta.parentNode) document.head.removeChild(themeMeta);
        if (apple && apple.parentNode) document.head.removeChild(apple);
        created.forEach((m) => { if (m.parentNode) document.head.removeChild(m); });
      } catch (e) { /* noop */ }
      document.title = prevTitle;
    };
  }, []);

  // Capture install prompt + detect standalone
  useEffect(() => {
    const checkStandalone = () => {
      const isStandalone = window.matchMedia('(display-mode: standalone)').matches
        || window.navigator.standalone === true
        || window.matchMedia('(display-mode: minimal-ui)').matches;
      if (isStandalone) setInstalled(true);
    };
    checkStandalone();
    const onBeforeInstall = (e) => { e.preventDefault(); setInstallPromptEvt(e); };
    const onAppInstalled = () => { setInstalled(true); setInstallPromptEvt(null); };
    window.addEventListener('beforeinstallprompt', onBeforeInstall);
    window.addEventListener('appinstalled', onAppInstalled);
    return () => {
      window.removeEventListener('beforeinstallprompt', onBeforeInstall);
      window.removeEventListener('appinstalled', onAppInstalled);
    };
  }, []);

  // Fetch masked metadata for the verify screen (role + last4)
  useEffect(() => {
    if (verified) return;
    fetch(`${API}/api/feedback-app/${token}/meta`)
      .then(r => r.ok ? r.json() : Promise.reject(r))
      .then(setMeta)
      .catch(() => setMeta(null));
  }, [token, verified]);

  const triggerInstall = async () => {
    if (installPromptEvt) {
      try { await installPromptEvt.prompt(); await installPromptEvt.userChoice; }
      catch (e) { /* noop */ }
      setInstallPromptEvt(null);
      return;
    }
    const ua = navigator.userAgent || '';
    const isIos = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
    if (isIos) setShowIosHint(true);
    else alert('Use your browser menu → "Add to Home screen" / "Install app" to install.');
  };

  const verifyMyPhone = async () => {
    setVerifyErr('');
    const phone = verifyPhone.trim();
    if (!/^\d{10}$/.test(phone)) return setVerifyErr('Enter 10-digit mobile without spaces or +91');
    setVerifying(true);
    try {
      const r = await fetch(`${API}/api/feedback-app/${token}/verify-phone`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone }),
      });
      if (!r.ok) { const d = await r.json().catch(() => ({})); throw new Error(d.detail || 'Verification failed'); }
      const data = await r.json();
      try { localStorage.setItem(bindKey, data.bind_id); } catch (e) { /* noop */ }
      setVerified(true);
    } catch (e) { setVerifyErr(e.message); }
    setVerifying(false);
  };

  const unbindDevice = () => {
    try { localStorage.removeItem(bindKey); localStorage.removeItem('rgv_fb_last_token'); } catch (e) { /* noop */ }
    setVerified(false); setInfo(null);
  };

  useEffect(() => {
    if (!verified) { setLoading(false); return; }
    fetch(`${API}/api/feedback-app/${token}`)
      .then(r => r.ok ? r.json() : Promise.reject(r))
      .then(d => setInfo(d))
      .catch(async e => { const txt = await e.text?.().catch(() => 'Invalid link'); setError(txt || 'Invalid link'); })
      .finally(() => setLoading(false));
  }, [token, verified]);

  const loadStats = () => {
    fetch(`${API}/api/feedback-app/${token}/stats?month=${month}`)
      .then(r => r.json()).then(setStats).catch(() => {});
  };

  useEffect(() => { if (info) loadStats(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [info, month, justSubmitted]);

  const submit = async () => {
    setError('');
    if (!villaId) return setError('Please select a villa');
    if (!guestPhone && !guestEmail) return setError('Guest phone or email required');
    if (Object.values(ratings).some(v => v < 1)) return setError('Please rate all 5 categories');
    setSubmitting(true);
    try {
      const res = await fetch(`${API}/api/feedback-app/${token}/submit`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ villa_id: villaId, guest_phone: guestPhone, guest_email: guestEmail, ratings, private_note: privateNote })
      });
      if (!res.ok) { const d = await res.json().catch(() => ({})); throw new Error(d.detail || 'Submit failed'); }
      const d = await res.json();
      setJustSubmitted(d);
      // reset form
      setRatings({ cleaning: 0, communication: 0, checkin: 0, checkout: 0, upkeep: 0 });
      setPrivateNote(''); setGuestPhone(''); setGuestEmail(''); setVillaId('');
      setGuestLoggedIn(false);
    } catch (e) { setError(e.message); }
    setSubmitting(false);
  };

  const guestLogin = () => {
    setLoginError('');
    if (!villaId) return setLoginError('Please select the villa first');
    if (!guestPhone && !guestEmail) return setLoginError('Enter your mobile or email to continue');
    if (guestPhone && !/^\d{10}$/.test(guestPhone.trim())) return setLoginError('Mobile must be 10 digits');
    if (guestEmail && !/^\S+@\S+\.\S+$/.test(guestEmail.trim())) return setLoginError('Enter a valid email');
    setGuestLoggedIn(true);
  };

  const logout = () => {
    try { window.close(); } catch (_) { /* ignore — browser blocks in some contexts */ }
    setTimeout(() => { window.location.href = 'about:blank'; }, 100);
  };

  if (!verified) {
    return (
      <div className="min-h-screen bg-[#0F1420] text-white p-6 flex items-center justify-center" data-testid="verify-gate">
        <div className="max-w-sm w-full">
          <div className="bg-gradient-to-r from-[#F57C20] via-[#FF9A4D] to-[#F57C20] py-3 px-4 rounded-t-xl">
            <p className="text-white text-xs font-bold tracking-widest text-center uppercase">
              ROYAL GARDEN VILLAS — Guest Feedback
            </p>
          </div>
          <div className="bg-[#1B2432] rounded-b-xl p-6 border border-white/5">
            <FiLock className="w-8 h-8 mx-auto mb-3 text-[#F57C20]" />
            <h2 className="text-lg font-semibold text-center mb-1">Verify Your Mobile</h2>
            <p className="text-xs text-white/60 text-center mb-4">
              This link is locked to one mobile number.{meta?.phone_last4 ? <> Enter the 10-digit number ending in <span className="text-[#F57C20] font-bold">{meta.phone_last4}</span>.</> : null}
            </p>
            <input
              type="tel" inputMode="numeric" pattern="\d{10}" maxLength={10}
              value={verifyPhone} onChange={e => setVerifyPhone(e.target.value.replace(/\D/g, ''))}
              placeholder="10-digit mobile" data-testid="verify-phone-input"
              className="w-full bg-[#0F1420] text-white text-center text-lg tracking-wider px-4 py-3 rounded border border-white/10 mb-3"
            />
            {verifyErr && <p className="text-red-400 text-xs mb-2 text-center" data-testid="verify-error">{verifyErr}</p>}
            <button onClick={verifyMyPhone} disabled={verifying}
              data-testid="verify-btn"
              className="w-full bg-[#F57C20] hover:bg-[#e56d10] text-white font-bold py-3 rounded disabled:opacity-50">
              {verifying ? 'Verifying…' : 'Unlock App'}
            </button>
            {meta?.role && (
              <p className="text-[10px] text-white/40 text-center mt-3">Role: {meta.role.toUpperCase()}</p>
            )}
          </div>
        </div>
      </div>
    );
  }

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-[#0F1420] text-white">Loading…</div>;
  if (error && !info) return <div className="min-h-screen flex items-center justify-center bg-[#0F1420] text-white p-6"><div className="max-w-md text-center"><FiLock className="w-10 h-10 mx-auto mb-3 text-[#F57C20]" /><p className="font-semibold mb-1">Access Denied</p><p className="text-sm text-white/70">{error}</p></div></div>;

  const role = info?.role;

  return (
    <div className="min-h-screen bg-[#0F1420] text-white pb-24">
      <div className="bg-gradient-to-r from-[#F57C20] via-[#FF9A4D] to-[#F57C20] py-3 px-4 flex items-center justify-between gap-2">
        <p className="text-white text-xs sm:text-sm font-bold tracking-widest uppercase flex-1 text-center">
          ROYAL GARDEN VILLAS — Guest Feedback
        </p>
        {!installed && (
          <button onClick={triggerInstall}
            data-testid="install-app-btn"
            className="bg-white/95 text-[#1A1A2E] text-[10px] font-bold px-2 py-1 rounded uppercase tracking-wider whitespace-nowrap">
            Install App
          </button>
        )}
      </div>
      {showIosHint && (
        <div className="bg-[#1B2432] border-l-4 border-[#F57C20] text-white text-xs px-4 py-3 flex items-center justify-between gap-2" data-testid="ios-install-hint">
          <span>On iPhone: tap <b>Share</b> → <b>Add to Home Screen</b> to install.</span>
          <button onClick={() => setShowIosHint(false)} className="text-white/60">✕</button>
        </div>
      )}

      {/* Live Stats — host sees directly; staff must reveal via toggle (hides earnings from guest during rating) */}
      {stats && (
        <div className="max-w-lg mx-auto p-4">
          {role === 'staff' && (
            <button
              onClick={() => setShowStats(s => !s)}
              data-testid="toggle-stats"
              className="w-full bg-[#1B2432] hover:bg-[#243044] text-white text-xs font-semibold py-2.5 px-4 rounded-xl mb-3 flex items-center justify-between border border-white/10"
            >
              <span className="flex items-center gap-2">
                <FiLock className="w-3.5 h-3.5 text-[#F57C20]" />
                {showStats ? 'Hide Summary' : 'Summary'}
              </span>
              <span className="text-[#F57C20]">{showStats ? '▲' : '▼'}</span>
            </button>
          )}

          {(role === 'host' || (role === 'staff' && showStats)) && (
          <div className="bg-[#1B2432] rounded-xl p-4 mb-4" data-testid="stats-card">
            <div className="flex items-center justify-between mb-3">
              <div>
                <p className="text-[#F57C20] text-[10px] tracking-widest uppercase">This Month</p>
                <p className="text-2xl font-bold">{stats.total_stars} <span className="text-[#F57C20] text-sm">stars</span></p>
                <p className="text-[10px] text-white/60">Cap: 300 stars / Rs.3,000</p>
              </div>
              <div className="text-right">
                <p className="text-[10px] text-white/60">Variable pay</p>
                <p className="text-2xl font-bold text-[#F57C20]">Rs.{stats.variable_pay.toLocaleString()}</p>
              </div>
            </div>
            <input type="month" value={month} onChange={e => setMonth(e.target.value)}
              className="bg-[#0F1420] text-white text-xs px-2 py-1 rounded border border-white/10 w-full" />
          </div>
          )}

          {/* Staff: villa readiness photo capture — inside Summary dropdown */}
          {role === 'staff' && showStats && (
            <StaffPhotoCapture token={token} />
          )}

          {/* Host-only: private notes feed with booking details */}
          {role === 'host' && stats.submissions?.some(s => s.private_note) && (
            <div className="bg-[#1B2432] rounded-xl p-4 mb-4" data-testid="host-private-notes">
              <p className="text-[#F57C20] text-[10px] tracking-widest uppercase mb-2">Private Notes to Host</p>
              {stats.submissions.filter(s => s.private_note).map(s => {
                const villaName = VILLAS.find(v => v.id === s.villa_id)?.label || s.villa_id;
                const posted = new Date(s.created_at);
                return (
                  <div key={s.id} className="mb-3 pb-3 border-b border-white/5 last:border-0" data-testid={`note-${s.id}`}>
                    {s.booking_guest_name ? (
                      <div className="flex items-center justify-between mb-1">
                        <p className="text-sm font-semibold text-white">{s.booking_guest_name}</p>
                        <span className="text-[9px] bg-[#F57C20]/20 text-[#F57C20] px-2 py-0.5 rounded uppercase tracking-wider">{s.total_stars}★</span>
                      </div>
                    ) : (
                      <p className="text-sm font-semibold text-white/80 mb-1">Guest (unmatched)</p>
                    )}
                    <p className="text-[11px] text-white/60">
                      {villaName}
                      {s.booking_check_in && <> · <span className="text-white/80">{s.booking_check_in} → {s.booking_check_out}</span></>}
                    </p>
                    <p className="text-sm text-white mt-2 leading-snug">&ldquo;{s.private_note}&rdquo;</p>
                    <p className="text-[10px] text-white/40 mt-2">
                      Posted {posted.toLocaleDateString()} at {posted.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      {s.guest_phone && <> · From {s.guest_phone}</>}
                      {s.guest_email && !s.guest_phone && <> · From {s.guest_email}</>}
                    </p>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Submission form — only for staff role (host is view-only) */}
      {role === 'staff' && (
        <div className="max-w-lg mx-auto px-4">
          {justSubmitted && (
            <div className="bg-green-600/20 border border-green-500 rounded-xl p-4 mb-4 text-center" data-testid="success-toast">
              <p className="text-lg font-bold">Thank you!</p>
              <p className="text-sm">Rated {justSubmitted.total_stars}★ (worth Rs.{justSubmitted.rupee_value})</p>

              {justSubmitted.thank_you_coupon && (
                <div className="mt-3 bg-gradient-to-br from-[#F57C20] to-[#e56d10] text-white rounded-lg p-4 border-2 border-dashed border-white/60" data-testid="thankyou-coupon">
                  <p className="text-[10px] tracking-widest uppercase opacity-90">Your Thank-You Gift</p>
                  <p className="text-2xl font-extrabold mt-1">Rs.{justSubmitted.thank_you_coupon.amount.toLocaleString()} OFF</p>
                  <p className="text-[11px] opacity-90">on your next stay at Royal Garden Villas</p>
                  <div className="mt-2 bg-white/95 text-[#1A1A2E] rounded px-3 py-2 font-mono text-lg font-bold tracking-wider" data-testid="thankyou-coupon-code">
                    {justSubmitted.thank_you_coupon.code}
                  </div>
                  <p className="text-[11px] mt-2 opacity-95 font-semibold" data-testid="coupon-instruction">
                    Click a Picture of the coupon Code through your mobile &amp; Save it for your Next Booking
                  </p>
                  <p className="text-[10px] mt-1 opacity-80">Locked to your mobile/email.</p>
                </div>
              )}

              {justSubmitted.total_stars === 25 && justSubmitted.feedback_id && (
                <PublishFiveStar
                  feedbackId={justSubmitted.feedback_id}
                  onDone={() => setJustSubmitted(js => ({ ...js, _published: true }))}
                  published={justSubmitted._published}
                />
              )}

              <button onClick={logout} className="mt-3 bg-white text-[#0F1420] px-4 py-2 rounded text-sm font-semibold" data-testid="logout-btn">
                <FiLogOut className="inline w-4 h-4 mr-1" /> Logout &amp; Close
              </button>
            </div>
          )}

          <div className="bg-[#1B2432] rounded-xl p-5">
            <p className="text-[#F57C20] text-[10px] tracking-widest uppercase mb-3">Guest Rating (Staff device)</p>

            <label className="text-xs text-white/70">Villa</label>
            <select value={villaId} onChange={e => setVillaId(e.target.value)} data-testid="villa-select"
              disabled={guestLoggedIn}
              className="w-full bg-[#0F1420] text-white text-sm px-3 py-2 rounded border border-white/10 mb-3 disabled:opacity-60">
              <option value="">Select villa…</option>
              {VILLAS.map(v => <option key={v.id} value={v.id}>{v.label}</option>)}
            </select>

            <label className="text-xs text-white/70">Guest Mobile (used for booking)</label>
            <input type="tel" value={guestPhone} onChange={e => setGuestPhone(e.target.value)}
              placeholder="10 digits" data-testid="guest-phone" disabled={guestLoggedIn}
              className="w-full bg-[#0F1420] text-white text-sm px-3 py-2 rounded border border-white/10 mb-2 disabled:opacity-60" />
            <label className="text-xs text-white/70">or Guest Email</label>
            <input type="email" value={guestEmail} onChange={e => setGuestEmail(e.target.value)}
              placeholder="guest@example.com" data-testid="guest-email" disabled={guestLoggedIn}
              className="w-full bg-[#0F1420] text-white text-sm px-3 py-2 rounded border border-white/10 mb-4 disabled:opacity-60" />

            {!guestLoggedIn && (
              <>
                {loginError && <p className="text-red-400 text-xs mb-2" data-testid="login-error">{loginError}</p>}
                <button onClick={guestLogin} data-testid="guest-login-btn"
                  className="w-full bg-[#F57C20] hover:bg-[#e56d10] text-white font-bold py-3 rounded mb-2">
                  Login to Rate
                </button>
                <p className="text-[10px] text-white/50 text-center">Enter mobile or email, then Login to reveal the rating stars.</p>
              </>
            )}

            {guestLoggedIn && (
              <>
            <div className="bg-white rounded-lg p-4 text-[#1A1A2E]" data-testid="rating-panel">
              {CATEGORIES.map(c => (
                <StarRow key={c.key} label={c.label} desc={c.desc}
                  value={ratings[c.key]} onChange={n => setRatings({ ...ratings, [c.key]: n })} />
              ))}
            </div>

            <div className="mt-4">
              <label className="text-xs text-white/70 font-semibold">Private Note to Host (visible only to Host)</label>
              <textarea value={privateNote} onChange={e => setPrivateNote(e.target.value)}
                data-testid="private-note" maxLength={2000} rows={3}
                placeholder="Optional. Only the Host will see this."
                className="w-full bg-[#0F1420] text-white text-sm px-3 py-2 rounded border border-white/10 mt-1" />
            </div>

            {error && <p className="text-red-400 text-xs mt-3" data-testid="error">{error}</p>}

            <button onClick={submit} disabled={submitting} data-testid="submit-btn"
              className="w-full bg-[#F57C20] text-white font-bold py-3 rounded mt-4 disabled:opacity-50">
              {submitting ? 'Submitting…' : 'Submit Rating'}
            </button>

            <button onClick={() => { setGuestLoggedIn(false); setRatings({ cleaning: 0, communication: 0, checkin: 0, checkout: 0, upkeep: 0 }); setPrivateNote(''); }}
              data-testid="guest-logout-btn"
              className="w-full text-white/60 hover:text-white text-xs py-2 mt-2 underline">
              Cancel &amp; go back
            </button>
              </>
            )}
          </div>
        </div>
      )}

      {/* Host: view-only */}
      {role === 'host' && (
        <div className="max-w-lg mx-auto px-4 pb-6 space-y-4">
          <div className="bg-[#1B2432] rounded-xl p-4 text-xs text-white/70">
            Host view — ratings shown live as staff submits them. Enable browser notifications for real-time push alerts.
            <button onClick={async () => {
              try {
                const perm = await Notification.requestPermission();
                if (perm === 'granted') alert('Notifications enabled.');
              } catch (_) { alert('Notifications not supported on this browser.'); }
            }} className="mt-2 block bg-[#F57C20] text-white text-xs px-3 py-1.5 rounded" data-testid="enable-notifs">
              Enable Push Notifications
            </button>
          </div>

          <div className="bg-[#1B2432] rounded-xl p-4" data-testid="host-photos">
            <p className="text-[#F57C20] text-[10px] tracking-widest uppercase mb-2 font-semibold">Villa Readiness Photos</p>
            <HostPhotosGallery token={token} />
          </div>

          <div className="bg-[#1B2432] rounded-xl p-4" data-testid="host-moderation">
            <p className="text-[#F57C20] text-[10px] tracking-widest uppercase mb-2 font-semibold">Home Page Reviews — Moderate</p>
            <ReviewModerationPanel
              apiBase={`${API}/api/feedback-app/${token}/reviews`}
              authConfig={{}}
              variant="host"
            />
          </div>

          <TrialWipePanel token={token} />
        </div>
      )}

      {/* Global footer — device unbind so user can move link to another device */}
      <div className="max-w-lg mx-auto px-4 pt-2 pb-8 text-center">
        <button onClick={unbindDevice} data-testid="unbind-device-btn"
          className="text-white/40 hover:text-white/70 text-[10px] underline">
          Not this phone? Unbind device
        </button>
      </div>
    </div>
  );
}
